/**
 * Утилиты палетного листа: чистые функции для сборки данных печатной формы.
 * Используются модулем ShiftPalletList и unit-тестами.
 */

/** Строка таблицы «Дата / № п. / ШТ» палетного листа */
export interface PalletListRow {
  date: string;
  batch_number: string;
  quantity: string;
}

/** Данные шапки палетного листа */
export interface PalletListData {
  product_name: string;
  trademark: string;
  container: string;
  barcode: string;
  filled_at: string;
  full_name: string;
  rows: PalletListRow[];
}

/**
 * Торговая марка из полного названия продукта: подстрока после маркера
 * «тм »/«TM » (регистронезависимо). Значение в кавычках/скобках обрезается
 * по закрывающий символ («тм "ОСТБЕРГ" хвост» → «ОСТБЕРГ»), без обрамления —
 * до конца строки/первой кавычки-скобки.
 * «Кремчиз "Шеф М" [А] Творожный тм ОСТБЕРГ» → «ОСТБЕРГ».
 */
export function extractTrademark(productName: string | null | undefined): string {
  if (!productName) return '';
  const match = productName.match(/(?:тм|tm)\s+(.+)/i);
  if (!match) return '';

  let value = match[1].trim();
  const closingByOpening: Record<string, string> = { '"': '"', '(': ')', '[': ']' };
  const closing = closingByOpening[value[0]];

  if (closing) {
    const end = value.indexOf(closing, 1);
    value = end > 0 ? value.slice(1, end) : value.slice(1);
  } else {
    const cut = value.search(/["\])]/);
    if (cut > 0) value = value.slice(0, cut);
  }

  return value.trim();
}

/** Короткое название продукта из полного имени: первые кавычки
 * («Кремчиз "Шеф Т" тм ОСТБЕРГ» → «Шеф Т»), иначе само название.
 * Шапку палетного листа печатает полное наименование; короткое живёт
 * только в имени листа Excel и имени файла (лимиты 31/60 символов). */
export function extractShortName(productName?: string | null): string {
  if (!productName) return '';
  const match = productName.match(/"([^"]+)"/);
  return (match && match[1]) ? match[1] : productName;
}

/** Человекочитаемый вес тары: «5.600» → «5,6»; null → пустая строка */
export function formatContainerWeight(weight: number | string | null | undefined): string {
  if (weight === null || weight === undefined || weight === '') return '';
  const parsed = typeof weight === 'string' ? parseFloat(weight.replace(',', '.')) : weight;
  if (isNaN(parsed) || parsed <= 0) return '';
  // убираем хвостовые нули, разделитель — запятая
  return String(parseFloat(parsed.toFixed(3))).replace('.', ',');
}

/** Контрольная цифра EAN-13 сходится (взвешенная сумма первых 12 цифр) */
export function isEan13ChecksumValid(code: string): boolean {
  if (!/^\d{13}$/.test(code)) return false;
  const sum = code.slice(0, 12).split('').reduce((acc, ch, i) => acc + Number(ch) * (i % 2 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === Number(code[12]);
}

/**
 * Формат штрихкода для JsBarcode. Явный формат тары (product_containers.
 * barcode_format из CRM, синхронизируется db_sync) главнее эвристики:
 *  - «GS1-128» → CODE128 всегда (JsBarcode не имеет рендера GS1-128, CODE128 —
 *    его носитель), даже если значение похоже на валидный EAN-13;
 *  - «EAN13» → EAN13; значение, которое EAN13 не может отрисовать (не 13 цифр
 *    или битая контрольная цифра), — фолбэк CODE128 (вызывающий показывает
 *    предупреждение);
 *  - не задан (или неизвестное значение) — прежнее авто-определение: 13 цифр
 *    с корректной контрольной цифрой → EAN13, иначе CODE128 (13 цифр с битой
 *    контрольной JsBarcode отказывается рисовать — рисуем CODE128, значение
 *    напечатано точно).
 */
export function barcodeFormat(
  barcode: string | null | undefined,
  format?: string | null,
): string {
  const value = (barcode || '').trim();
  const explicit = (format || '').trim();

  if (explicit === 'GS1-128') return 'CODE128';

  // пустой или неизвестный формат — авто-определение (устойчивость к мусору)
  const auto = explicit !== 'EAN13';
  const wantEan13 = explicit === 'EAN13' || (auto && /^\d{13}$/.test(value));
  return wantEan13 && isEan13ChecksumValid(value) ? 'EAN13' : 'CODE128';
}

/**
 * Предупреждение о несоответствии формата: штрихкод должен был напечататься
 * EAN-13 (формат задан явно или выбран эвристикой), но отрисован фолбэком
 * CODE128 — значение не является корректным EAN-13.
 */
export function isEan13Fallback(
  barcode: string | null | undefined,
  format?: string | null,
): boolean {
  const explicit = (format || '').trim();
  if (explicit === 'GS1-128') return false;
  const wantedEan13 = explicit === 'EAN13'
    || /^\d{13}$/.test((barcode || '').trim());
  return wantedEan13 && barcodeFormat(barcode, format) === 'CODE128';
}

/**
 * Текст предупреждения, когда EAN-13 не отрисовался и штрихкод напечатан
 * фолбэком CODE128 (формат задан явно или выбран эвристикой, а значение
 * некорректно). null — предупреждения нет. Единый текст для превью формы
 * и выгрузки XLSX.
 */
export function ean13FallbackWarning(
  barcode: string | null | undefined,
  format?: string | null,
): string | null {
  // пустой/пробельный штрихкод — отдельное предупреждение «не заполнен»,
  // текст фолбэка к нему не примешиваем
  if (!(barcode || '').trim()) return null;
  if (!isEan13Fallback(barcode, format)) return null;
  return /^\d{13}$/.test((barcode || '').trim())
    ? 'Контрольная цифра EAN-13 не сходится — штрихкод отрисован как CODE128. '
      + 'Проверьте значение поля «Штрихкод» у тары в CRM.'
    : 'Значение не подходит для формата EAN-13 — штрихкод отрисован как CODE128. '
      + 'Проверьте поля «Штрихкод» и «Формат» у тары в CRM.';
}

/**
 * Предзаполнение палетного листа из факта: одна строка с датой смены,
 * номером партии и количеством из факта + пустые строки под ручное
 * заполнение (как на бумажном образце).
 */
export function buildPalletListRows(
  shiftDate: string | null | undefined,
  batchNumber: string | null | undefined,
  quantity: number | string | null | undefined,
  emptyRows: number = 9,
): PalletListRow[] {
  const date = (shiftDate || '').slice(0, 10).split('-').reverse().join('.');
  const rows: PalletListRow[] = [
    {
      date,
      batch_number: batchNumber || '',
      quantity: quantity !== null && quantity !== undefined && quantity !== '' ? String(quantity) : '',
    },
  ];
  for (let i = 0; i < emptyRows; i++) {
    rows.push({ date: '', batch_number: '', quantity: '' });
  }
  return rows;
}

/** Дата-время заполнения «19.10.2026 14:30» по местным часам */
export function formatFilledAt(date: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Факт смены для группировки палетного листа (поля выборки ShiftPalletList) */
export interface PalletFactDoc {
  number: string | null | undefined;
  product_name: string | null | undefined;
  barcode: string | null | undefined;
  /** формат штрихкода тары из CRM (product_containers.barcode_format): GS1-128 / EAN13 / null */
  barcode_format?: string | null | undefined;
  container_weight: number | string | null | undefined;
  shift_date: string | null | undefined;
  quantity: number | string | null | undefined;
}

/**
 * Группа палетного листа: один продукт с одной тарой (SKU) и все его партии
 * смены — лист выгрузки за смену группируется по SKU, партии заполняют строки
 * таблицы «Дата / № п. / шт» (по возрастанию номера партии), остальные
 * строки до 11 — пустые под ручное заполнение.
 */
export interface PalletSkuGroup {
  short_name: string;
  product_name: string;
  trademark: string;
  container: string;
  barcode: string;
  /** формат штрихкода тары из CRM (GS1-128 / EAN13 / пусто — авто) */
  barcode_format: string;
  /** номера партий группы (для заголовка и имени листа) */
  numbers: string[];
  rows: PalletListRow[];
}

/** Числовой порядок номеров партий ('2' < '10'); нечисловые — строковое сравнение */
function compareBatchNumbersPlain(a?: string | null, b?: string | null): number {
  return String(a ?? '').localeCompare(String(b ?? ''), 'ru', { numeric: true });
}

/** Работник смены из выборки ShiftWorkers (поля employee_name/role_id/is_manager) */
export interface ShiftWorkerDoc {
  employee_name: string | null | undefined;
  role_id: number | null | undefined;
  is_manager: boolean | null | undefined;
}

/**
 * Мастер смены для палетного листа (предзаполнение «ФИО»): работник
 * с ролью-мастером (voc_roles.is_manager); при нескольких приоритет
 * «Старшему мастеру» (минимальный id роли: «Старший мастер» — 1,
 * «Мастер на АТОМе» — 6). Нет мастеров — пустая строка (ФИО заполняют
 * на производстве).
 */
export function pickShiftMaster(workers: ShiftWorkerDoc[]): string {
  const managers = (workers || [])
    .filter((w) => !!w.is_manager)
    .sort((a, b) => (a.role_id ?? Number.MAX_SAFE_INTEGER) - (b.role_id ?? Number.MAX_SAFE_INTEGER));
  return managers.length ? (managers[0].employee_name || '') : '';
}

/**
 * Группировка фактов смены по SKU (продукт + тара + штрихкод) для листа
 * смены. Группы идут в порядке первого появления факта, партии внутри —
 * по номеру. Таблица: строки всех партий группы + пустые до 11 строк.
 */
export function groupFactsBySku(documents: PalletFactDoc[], tableRows: number = 11): PalletSkuGroup[] {
  const groups: PalletSkuGroup[] = [];
  const byKey = new Map<string, PalletSkuGroup>();

  for (const doc of documents || []) {
    const product_name = doc.product_name || '';
    const container = formatContainerWeight(doc.container_weight);
    const barcode = doc.barcode || '';
    const barcode_format = doc.barcode_format || '';
    const key = `${product_name}|${container}|${barcode}`;
    let group = byKey.get(key);
    if (!group) {
      group = {
        short_name: extractShortName(product_name),
        product_name,
        trademark: extractTrademark(product_name),
        container,
        barcode,
        barcode_format,
        numbers: [],
        rows: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }

    group.numbers.push(doc.number || '');
    const date = (doc.shift_date || '').slice(0, 10).split('-').reverse().join('.');
    group.rows.push({
      date,
      batch_number: doc.number || '',
      quantity: doc.quantity !== null && doc.quantity !== undefined && doc.quantity !== '' ? String(doc.quantity) : '',
    });
  }

  for (const group of groups) {
    const order = group.numbers.map((_, i) => i).sort((a, b) =>
      compareBatchNumbersPlain(group.numbers[a], group.numbers[b]));
    group.numbers = order.map((i) => group.numbers[i]);
    group.rows = order.map((i) => group.rows[i]);
    while (group.rows.length < tableRows) {
      group.rows.push({ date: '', batch_number: '', quantity: '' });
    }
  }

  return groups;
}
